import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { applyLang, getCurrentLang } from '../utils/i18n';
import {
  sitesApi, circuitsApi,
  SITE_CATEGORIES, CAT_LABEL, DIFF_LABEL,
} from '../services/tourismeApi';
import './Tourisme.css';

const CAT_ACCENT = {
  religieux:   '#E76F51',
  culturel:    '#1B6985',
  nature:      '#34A853',
  patrimoine:  '#C9A961',
  gastronomie: '#A93D29',
};

function siteImage(s) {
  if (s.mainImageUrl) return s.mainImageUrl;
  const seed = s.mainImageSeed || `cb-tour-${s.id}`;
  return `https://picsum.photos/seed/${seed}/1200/900`;
}
function circuitImage(c) {
  if (c.mainImageUrl) return c.mainImageUrl;
  const seed = c.mainImageSeed || `cb-tour-circuit-${c.id}`;
  return `https://picsum.photos/seed/${seed}/1200/900`;
}

const Tourisme = () => {
  const [sites, setSites]       = useState([]);
  const [circuits, setCircuits] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.tour-page .reveal').forEach((el) => io.observe(el));
    applyLang(getCurrentLang());
    return () => io.disconnect();
  }, [sites, circuits, filterCategory]);

  useEffect(() => {
    let cancelled = false;
    sitesApi.list({
      category: filterCategory !== 'all' ? filterCategory : undefined,
      limit: 100,
    })
      .then((data) => { if (!cancelled) setSites(data.items); })
      .catch(()    => { if (!cancelled) setSites([]); });
    return () => { cancelled = true; };
  }, [filterCategory]);

  useEffect(() => {
    let cancelled = false;
    circuitsApi.list({ limit: 50 })
      .then((data) => { if (!cancelled) setCircuits(data.items); })
      .catch(()    => { if (!cancelled) setCircuits([]); });
    return () => { cancelled = true; };
  }, []);

  const featured = useMemo(() => sites.filter((s) => s.featured), [sites]);
  const others   = useMemo(() => sites.filter((s) => !s.featured), [sites]);

  return (
    <main className="tour-page">
      {/* ============== EN-TÊTE SOBRE (sans image) ============== */}
      <header className="tour-head">
        <div className="wrap tour-head-inner reveal">
          <span className="tour-head-eyebrow">Tourisme · Cambérène · Layène</span>
          <h1>
            Un village,<br />
            <span className="ital">à arpenter.</span>
          </h1>
          <p className="lede">
            De l'Appel sur la plage à la rumeur du débarcadère à l'aube — Cambérène
            se vit autant qu'elle se visite. Sites et parcours en accès libre,
            de la mosquée Layène jusqu'à la rue&nbsp;8.
          </p>

          <div className="tour-head-stats">
            <div className="tour-head-stat"><b>{sites.length || '—'}</b><span>Sites référencés</span></div>
            <div className="tour-head-stat"><b>{circuits.length || '—'}</b><span>Circuits guidés</span></div>
            <div className="tour-head-stat"><b>5</b><span>Catégories</span></div>
            <div className="tour-head-stat"><b>1884</b><span>Année de l'Appel</span></div>
          </div>
        </div>
      </header>

      {/* ============== QUOTE ============== */}
      <section className="tour-quote reveal">
        <div className="wrap">
          <p>« Pour visiter Cambérène, il faut marcher — entre la mosquée, le débarcadère et la plage où tout a commencé. »</p>
          <span className="src">Office du tourisme · Cambérène, 2026</span>
        </div>
      </section>

      {/* ============== SITES À VEDETTE ============== */}
      <section className="tour-section tour-sites">
        <div className="wrap">
          <div className="tour-section-head reveal">
            <div>
              <span className="eyebrow">Sites &amp; lieux</span>
              <h2>À <span className="ital">découvrir.</span></h2>
            </div>
            <p>
              Mosquée Layène, mausolée du Mahdi, débarcadère, plage de l'Appel —
              les lieux qui font Cambérène, en accès libre.
            </p>
          </div>

          <div className="tour-filters reveal">
            {SITE_CATEGORIES.map((c) => (
              <button
                key={c.id}
                className={`tour-pill ${filterCategory === c.id ? 'on' : ''}`}
                onClick={() => setFilterCategory(c.id)}
              >{c.lbl}</button>
            ))}
          </div>

          {/* Featured : grande vitrine pleine largeur (uniquement si pas de filtre) */}
          {filterCategory === 'all' && featured[0] && (
            <Link to={`/tourisme/${featured[0].slug}`} className="site-feature reveal">
              <div className="site-feature-photo">
                <div className="img" style={{ backgroundImage: `url('${siteImage(featured[0])}')` }} />
                <span className="cat" style={{ background: CAT_ACCENT[featured[0].category] }}>
                  {CAT_LABEL[featured[0].category]}
                </span>
              </div>
              <div className="site-feature-body">
                <span className="num">01 · En vedette</span>
                <h3>{featured[0].title}</h3>
                <p>{featured[0].shortDescription}</p>
                {featured[0].address && (
                  <span className="addr">📍 {featured[0].address.split(',')[0]}</span>
                )}
                <span className="cta">
                  Découvrir <span className="arrow">→</span>
                </span>
              </div>
            </Link>
          )}

          {/* Grille magazine 2 cols */}
          <div className="site-grid">
            {(filterCategory === 'all' ? [...featured.slice(1), ...others] : sites).map((s, i) => (
              <Link key={s.id} to={`/tourisme/${s.slug}`} className="site-card reveal">
                <div className="site-card-photo">
                  <div className="img" style={{ backgroundImage: `url('${siteImage(s)}')` }} />
                  <span className="num">{String(i + (filterCategory === 'all' ? 2 : 1)).padStart(2, '0')}</span>
                  <span className="cat" style={{ background: CAT_ACCENT[s.category] }}>
                    {CAT_LABEL[s.category]}
                  </span>
                </div>
                <div className="site-card-body">
                  <h3>{s.title}</h3>
                  <p>{s.shortDescription}</p>
                  <div className="site-card-foot">
                    {s.address
                      ? <span className="addr">📍 {s.address.split(',')[0]}</span>
                      : <span />}
                    <span className="arrow">→</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {sites.length === 0 && (
            <p className="tour-muted">Aucun site dans cette catégorie.</p>
          )}
        </div>
      </section>

      {/* ============== CIRCUITS ============== */}
      <section className="tour-section tour-circuits">
        <div className="wrap">
          <div className="tour-section-head reveal">
            <div>
              <span className="eyebrow">Circuits guidés</span>
              <h2>Itinéraires<br /><span className="ital">en village.</span></h2>
            </div>
            <p>
              Demi-journée spirituelle, immersion au débarcadère, journée complète —
              trois rythmes pour découvrir Cambérène autrement.
            </p>
          </div>

          <div className="circ-grid">
            {circuits.map((c) => (
              <article key={c.id} className="circ-card reveal">
                <Link to={`/tourisme/circuit/${c.slug}`} className="circ-card-link">
                  <div className="photo">
                    <div className="img" style={{ backgroundImage: `url('${circuitImage(c)}')` }} />
                    <div className="grad" />
                    <span className="diff">{DIFF_LABEL[c.difficulty]} · {c.duration || '—'}</span>
                  </div>
                  <div className="body">
                    <h3>{c.title}</h3>
                    <p>{c.shortDescription}</p>
                    <div className="stops">
                      {c.stops?.slice(0, 3).map((s, i) => (
                        <span key={i} className="stop">{i + 1}. {s.title}</span>
                      ))}
                      {c.stops?.length > 3 && <span className="stop more">+{c.stops.length - 3}</span>}
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============== CTA ============== */}
      <section className="tour-cta">
        <div className="wrap reveal">
          <span className="eyebrow">Préparer ma visite</span>
          <h3>Pour les groupes, le guide se contacte <span className="ital">à la mairie.</span></h3>
          <p>
            Pèlerinages organisés, visites scolaires, journées culturelles — l'office du tourisme
            local accompagne les visiteurs et coordonne les guides bénévoles.
          </p>
          <div className="tour-cta-actions">
            <Link to="/mairie" className="btn btn-primary">
              <span>Contacter la mairie</span>
              <span className="arrow">→</span>
            </Link>
            <Link to="/histoire" className="btn btn-outline">
              <span>L'histoire du village</span>
              <span className="arrow">↗</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Tourisme;
